import type { MarketedMembershipPlanType, MembershipPlanShape } from '@frontend/api-client';
import { getMembershipPlanDisplayPricing } from '@frontend/api-client';
import type { PromotionDetails } from '@frontend/api-types';
import { useEffect, useRef, useState } from '@frontend/react';
import { Button, LinkButton, Text, TextInput, XStack, YStack } from '@frontend/web-react';
import { safeCss } from '@frontend/web-utils';
import { PromotionBanner } from '../PromotionBanner';
import { PlanCard } from './PlanCard';

export type SelectPlanStepProps = {
  appliedPromoCode?: string;
  hasSuccessfullyAppliedPromoCode: boolean;
  isApplyPromoCodeLoading: boolean;
  isError: boolean;
  isLoading: boolean;
  onApplyPromoCode: (promoCode: string) => Promise<MaybeUndefined<PromotionDetails>>;
  onDismissPromo: () => void;
  onRefetch: () => void;
  onSelectPlan: (planType: MarketedMembershipPlanType) => void;
  pricingState: 'error' | 'initialLoading' | 'ready' | 'transitioning';
  promoCodeErrorMessage?: string;
  promotionDescription?: string;
  promotionName?: string;
  selectablePlans: Array<MembershipPlanShape & { planType: MarketedMembershipPlanType }>;
  selectedPlanType?: MarketedMembershipPlanType;
};

const formatEstimatedTasks = (min: number, max: number): string => {
  return `~ ${min} - ${max} tasks`;
};

export const SelectPlanStep = ({
  appliedPromoCode,
  hasSuccessfullyAppliedPromoCode,
  isApplyPromoCodeLoading,
  isError,
  isLoading,
  onApplyPromoCode,
  onDismissPromo,
  onRefetch,
  onSelectPlan,
  pricingState,
  promoCodeErrorMessage,
  promotionDescription,
  promotionName,
  selectablePlans,
  selectedPlanType,
}: SelectPlanStepProps) => {
  const [promoCodeInput, setPromoCodeInput] = useState(appliedPromoCode ?? '');
  const promoCodeInputRef = useRef<HTMLInputElement>(null);
  const hasInitializedPromoInput = useRef(false);

  useEffect(() => {
    if (appliedPromoCode && !hasInitializedPromoInput.current) {
      setPromoCodeInput(appliedPromoCode);
      // eslint-disable-next-line fp/no-mutation -- Ref mutation is the standard React pattern for tracking initialization
      hasInitializedPromoInput.current = true;
    }
  }, [appliedPromoCode]);

  const handleApply = async () => {
    if (!promoCodeInput.trim()) return;
    await onApplyPromoCode(promoCodeInput.trim());
    promoCodeInputRef.current?.blur();
  };

  const handleDismiss = () => {
    setPromoCodeInput('');
    onDismissPromo();
    promoCodeInputRef.current?.blur();
  };
  if (isLoading) {
    return (
      <YStack gapY="md">
        <YStack bg="lineGray" height={32} radius="md" width="60%" />
        <YStack gapY="md">
          {[1, 2, 3, 4].map((i) => (
            <YStack bg="lineGray" height={80} key={i} radius="lg" />
          ))}
        </YStack>
      </YStack>
    );
  }

  if (isError) {
    return (
      <YStack gapY="md">
        <Text type="h4">Your plans took a duck dive.</Text>
        <Text color="secondary" type="p-sm">
          {"Don't worry, we'll bring them back. Try again."}
        </Text>
        <Button label="Try again" onClick={onRefetch} size="sm" />
      </YStack>
    );
  }

  if (selectablePlans.length === 0) {
    return (
      <YStack gapY="md">
        <Text type="h4">No plans available</Text>
        <Text color="secondary" type="p-sm">
          There are no membership plans available at this time.
        </Text>
      </YStack>
    );
  }

  return (
    <YStack gapY="lg">
      {hasSuccessfullyAppliedPromoCode && promotionName ? (
        <PromotionBanner
          onDismiss={handleDismiss}
          promotionCode={appliedPromoCode}
          promotionDescription={promotionDescription}
          promotionName={promotionName}
        />
      ) : null}

      <YStack aria-label="Select a plan" gapY="md" role="radiogroup">
        {selectablePlans.map((plan, index) => {
          const isSelected = selectedPlanType === plan.planType;
          const isFocusable = isSelected || (!selectedPlanType && index === 0);
          const pricing = getMembershipPlanDisplayPricing(plan);

          return (
            <PlanCard
              estimatedTasks={formatEstimatedTasks(plan.estimatedTasksPerMonth.min, plan.estimatedTasksPerMonth.max)}
              isFocusable={isFocusable}
              isPricingLoading={pricingState === 'transitioning'}
              isSelected={isSelected}
              key={plan.planType}
              onSelect={() => onSelectPlan(plan.planType)}
              planLabel={plan.label}
              primaryPrice={pricing?.primaryPrice ?? plan.price}
              secondaryPrice={pricing?.secondaryPrice}
            />
          );
        })}
      </YStack>

      {!hasSuccessfullyAppliedPromoCode ? (
        <YStack gapY="xs">
          <Text type="h4">Promo code</Text>
          <XStack>
            <YStack flex={1}>
              <TextInput
                classNames={{ input: safeCss('rounded-md') }}
                disabled={isApplyPromoCodeLoading}
                error={promoCodeErrorMessage}
                onChange={(value) => setPromoCodeInput(value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && promoCodeInput.trim() && !isApplyPromoCodeLoading) {
                    void handleApply();
                  }
                }}
                placeholder="Enter promo code here"
                ref={promoCodeInputRef}
                rightSection={
                  <LinkButton
                    className="mr-xxl"
                    disabled={!promoCodeInput.trim() || isApplyPromoCodeLoading}
                    isLoading={isApplyPromoCodeLoading}
                    label="Apply"
                    onClick={() => {
                      void handleApply();
                    }}
                    withUnderline={false}
                  />
                }
                value={promoCodeInput}
              />
            </YStack>
          </XStack>
        </YStack>
      ) : null}

      <YStack p="sm">
        <Text color="secondary" type="caption">
          All plans include expert copilots to support you every step of the way; smart automation that anticipates and
          streamlines your tasks; exclusive, member-only perks and offers; and seamless syncing for effortless task
          management across devices.
        </Text>
      </YStack>
    </YStack>
  );
};
